'use client';

import React, { useState } from 'react';
import dynamic from 'next/dynamic';
import { SkeletonBlock } from '@/components/ui/LoadingSkeleton';

const RevenueAreaChart = dynamic(() => import('./charts/RevenueAreaChart'), {
  ssr: false,
  loading: () => <SkeletonBlock className="w-full h-64" />,
});

const OrderPipelineBarChart = dynamic(() => import('./charts/OrderPipelineBarChart'), {
  ssr: false,
  loading: () => <SkeletonBlock className="w-full h-64" />,
});

const dateRanges = [
  { id: 'range-7d', label: '7D' },
  { id: 'range-30d', label: '30D' },
  { id: 'range-90d', label: '90D' },
  { id: 'range-ytd', label: 'YTD' },
];

export default function ExecutiveChartsRow() {
  const [activeRange, setActiveRange] = useState('range-30d');

  return (
    <div className="grid grid-cols-1 lg:grid-cols-5 xl:grid-cols-5 2xl:grid-cols-5 gap-4">
      {/* Revenue Trend — 3 cols */}
      <div className="col-span-1 lg:col-span-3 xl:col-span-3 2xl:col-span-3 bg-card rounded-xl border border-border shadow-card p-5">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-sm font-700 text-foreground">Revenue Trend</h2>
            <p className="text-xs text-muted-foreground mt-0.5">Daily invoiced vs collected · XCD</p>
          </div>
          <div className="flex items-center gap-1 bg-muted rounded-lg p-0.5">
            {dateRanges?.map((r) => (
              <button
                key={r?.id}
                onClick={() => setActiveRange(r?.id)}
                className={`px-2.5 py-1 rounded-md text-xs font-600 transition-all duration-150 ${
                  activeRange === r?.id
                    ? 'bg-card text-primary shadow-card'
                    : 'text-muted-foreground hover:text-foreground'
                }`}
              >
                {r?.label}
              </button>
            ))}
          </div>
        </div>
        <div className="flex items-center gap-4 mb-3">
          {[
            { label: 'Invoiced', color: 'bg-primary' },
            { label: 'Collected', color: 'bg-positive' },
          ]?.map((l) => (
            <div key={`legend-${l?.label}`} className="flex items-center gap-1.5">
              <div className={`w-2.5 h-2.5 rounded-sm ${l?.color}`} />
              <span className="text-xs text-muted-foreground font-500">{l?.label}</span>
            </div>
          ))}
        </div>
        <RevenueAreaChart />
      </div>
      {/* Order Pipeline — 2 cols */}
      <div className="col-span-1 lg:col-span-2 xl:col-span-2 2xl:col-span-2 bg-card rounded-xl border border-border shadow-card p-5">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h2 className="text-sm font-700 text-foreground">Orders by Status</h2>
            <p className="text-xs text-muted-foreground mt-0.5">Current pipeline snapshot</p>
          </div>
          <span className="text-xs font-600 text-primary bg-secondary px-2 py-1 rounded-lg">Today</span>
        </div>
        <OrderPipelineBarChart />
        <div className="mt-3 grid grid-cols-2 gap-2">
          {[
            { label: 'Avg Order Value', value: 'XCD 8,935' },
            { label: 'Fill Rate', value: '97.3%' },
          ]?.map((s) => (
            <div key={`ord-stat-${s?.label}`} className="bg-muted rounded-lg p-2.5 text-center">
              <p className="font-mono-nums text-sm font-700 text-foreground">{s?.value}</p>
              <p className="text-2xs text-muted-foreground mt-0.5">{s?.label}</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}